import type { ViewThruSdkEnvironment } from '../viewthruSdkGlobal'

const SCRIPT_PATH = '/sdk/v1/viewthru-sdk.js'

const scriptUrlForEnvironment = (environment: ViewThruSdkEnvironment): string => {
    const host =
        environment === 'production'
            ? 'https://credit-api.affiliatech.co.uk'
            : 'https://credit-api.staging.affiliatech.co.uk'
    return `${host}${SCRIPT_PATH}`
}

let loadPromise: Promise<void> | null = null
let loadedScriptUrl: string | null = null

export const loadViewThruSdkScript = (environment: ViewThruSdkEnvironment): Promise<void> => {
    const url = scriptUrlForEnvironment(environment)

    if (typeof window === 'undefined') {
        return Promise.reject(new Error('ViewThru SDK can only load in the browser'))
    }

    if (window.ViewthruSDK && loadedScriptUrl === url) {
        return Promise.resolve()
    }

    if (loadPromise && loadedScriptUrl === url) {
        return loadPromise
    }

    loadedScriptUrl = url
    loadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[data-viewthru-sdk="${url}"]`)
        if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true })
            existing.addEventListener('error', () => reject(new Error(`Failed to load ViewThru SDK: ${url}`)), {
                once: true,
            })
            if (window.ViewthruSDK) resolve()
            return
        }

        const script = document.createElement('script')
        script.src = url
        script.async = true
        script.dataset.viewthruSdk = url
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load ViewThru SDK: ${url}`))
        document.head.appendChild(script)
    })

    return loadPromise
}

export const createViewThruSdk = (environment: ViewThruSdkEnvironment) => {
    if (!window.ViewthruSDK) {
        throw new Error('ViewThru SDK is not loaded')
    }
    return window.ViewthruSDK.init({ environment })
}
